// Opt-in "autoplay videos while scrolling" preference for the big paginated feeds
// (Explore / Profile), which render video tiles in `previewMode`: a first-frame
// poster + play badge, previewing on hover on desktop and never loading the clip
// inline on touch. That default keeps mobile data/memory bounded, but it removed
// the scroll-and-they-play browsing some users valued — this switch brings it back
// for anyone who asks for it.
//
// Browser-scoped (localStorage, not the account) like the Create-UI cookie, so it
// survives logout and stays per-device: a phone on cellular can keep autoplay off
// while a desktop has it on. Reads go through safeStorage because iOS Safari throws
// on localStorage access in Private Browsing / "Block All Cookies".

import { useEffect, useState } from 'react'

import { safeGetItem, safeSetItem } from './safeStorage'

export const VIDEO_AUTOPLAY_KEY = 'pc_video_autoplay'
// Same-tab broadcast: `storage` only fires in OTHER tabs, so toggling in Settings
// would not reach Cards already mounted in this one (e.g. behind the drawer).
const CHANGE_EVENT = 'pc:video-autoplay-changed'

export function readVideoAutoplay() {
  return safeGetItem(VIDEO_AUTOPLAY_KEY) === 'on'
}

export function setVideoAutoplay(enabled) {
  safeSetItem(VIDEO_AUTOPLAY_KEY, enabled ? 'on' : 'off')
  try {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT))
  } catch {
    // no window (SSR) — nothing is mounted to notify.
  }
  return enabled
}

// Starts false on every render path (SSR and first client paint) and resolves
// after mount, so an opted-in visitor can never cause a hydration mismatch — the
// same latch pattern Card already uses for hover capability.
export function useVideoAutoplayPreference() {
  const [enabled, setEnabled] = useState(false)

  useEffect(() => {
    const apply = () => setEnabled(readVideoAutoplay())
    apply()
    window.addEventListener(CHANGE_EVENT, apply)
    window.addEventListener('storage', apply)
    return () => {
      window.removeEventListener(CHANGE_EVENT, apply)
      window.removeEventListener('storage', apply)
    }
  }, [])

  return enabled
}
